/**
 * Utility function to remove problematic Ant Design 3.x tab navigation icons
 * that cause ARIA accessibility violations.
 *
 * These icons have aria-label attributes without proper role attributes,
 * which violates WCAG accessibility guidelines.
 */

/**
 * Removes tab navigation icons that cause accessibility issues
 * @param {HTMLElement|Document} [container] - Optional container to search within. Defaults to document.
 */
export const removeTabNavigationIcons = (container = document) => {
  // Target all problematic icon classes from Ant Design 3.x
  const problematicSelectors = [
    '.ant-tabs-tab-prev-icon-target',
    '.ant-tabs-tab-next-icon-target',
    '.ant-tabs-tab-prev-icon',
    '.ant-tabs-tab-next-icon',
  ].join(', ')

  const problematicIcons = container.querySelectorAll(problematicSelectors)

  problematicIcons.forEach(icon => {
    // Only remove if it has aria-label but no role (the problematic case)
    if (icon.hasAttribute('aria-label') && !icon.hasAttribute('role')) {
      if (icon.parentNode) {
        icon.parentNode.removeChild(icon)
      }
    }
  })
}

/**
 * Sets up automatic removal of tab navigation icons with proper cleanup
 * Use this in React components with useEffect
 * @returns {Function} Cleanup function
 */
export const useTabIconRemoval = () => {
  const setupRemoval = () => {
    // Remove icons immediately
    removeTabNavigationIcons()

    // Also remove icons after a short delay to catch dynamically rendered ones
    const timeoutId = setTimeout(() => {
      removeTabNavigationIcons()
    }, 100)

    return () => clearTimeout(timeoutId)
  }

  return setupRemoval
}

/**
 * Initialize tab icon removal for the entire application
 * Call this once in your main App component
 */
export const initializeGlobalTabIconRemoval = () => {
  // Remove existing icons
  removeTabNavigationIcons()

  // Set up a MutationObserver to remove icons as they're added
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // Check if any added nodes contain tab icons
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Cast to HTMLElement since we've confirmed it's an ELEMENT_NODE
            const element = node
            // Double-check it has the querySelectorAll method before using
            if ('querySelectorAll' in element) {
              removeTabNavigationIcons(element)
            }
          }
        })
      }
    })
  })

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // Return cleanup function
  return () => observer.disconnect()
}

export default removeTabNavigationIcons
